import { useEffect, useState } from "react";
import axios from "axios";
import { BookOpen, HandHeart, Users, Sparkles } from "lucide-react";
import IllustrationTile, { themeNameFromUrl } from "@/components/IllustrationTile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveUrl(u) {
  return u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

const values = [
  { icon: BookOpen, color: "bg-brand-yellow", label: "English-based learning", desc: "Rich language exposure through songs, stories and play." },
  { icon: HandHeart, color: "bg-brand-green", label: "Islamic ethos", desc: "Gentle values, kindness, gratitude and simple duas." },
  { icon: Users, color: "bg-brand-red text-white", label: "Small caring groups", desc: "Every child is seen, heard and cheered on." },
  { icon: Sparkles, color: "bg-brand-purple", label: "Play-based ECD", desc: "Early childhood development that feels like fun." },
];

export default function About() {
  const [aboutItem, setAboutItem] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/gallery?kind=about`).then((res) => {
      if (alive && Array.isArray(res.data) && res.data.length > 0) {
        const sorted = [...res.data].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        setAboutItem(sorted[0]);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const aboutTheme = aboutItem ? themeNameFromUrl(aboutItem.url) : null;
  const aboutPhotoUrl = aboutItem && !aboutTheme ? resolveUrl(aboutItem.url) : null;

  return (
    <section id="about" className="relative py-20 md:py-28" data-testid="about-section">
      <div className="mx-auto max-w-6xl px-6 grid gap-12 md:grid-cols-2 items-center">
        <div className="relative order-2 md:order-1">
          <div className="absolute -top-5 -left-4 rotate-[-6deg] bg-brand-yellow border-[3px] border-brand-ink rounded-2xl px-4 py-1.5 font-display font-semibold shadow-brutal-sm z-10">
            Since 2005 ✨
          </div>
          <div className="rounded-[32px] overflow-hidden border-[4px] border-brand-ink shadow-brutal-lg h-[440px]" data-testid="about-image-container">
            {aboutPhotoUrl ? (
              <img
                src={aboutPhotoUrl}
                alt={aboutItem?.label || "About Cheeky Monkeys"}
                className="w-full h-full object-cover"
                data-testid="about-image"
              />
            ) : (
              <IllustrationTile theme={aboutTheme || "palette"} />
            )}
          </div>
          <div className="absolute -bottom-6 -right-4 rotate-[6deg] bg-white border-[3px] border-brand-ink rounded-2xl px-4 py-2 shadow-brutal-sm">
            <p className="font-display font-bold text-brand-red text-2xl leading-none">100s</p>
            <p className="text-xs font-bold text-brand-ink">of graduates</p>
          </div>
        </div>

        <div className="order-1 md:order-2">
          <p className="uppercase tracking-widest text-sm font-bold text-brand-red">About Cheeky Monkeys</p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-brand-ink leading-tight">
            A little home for big <span className="text-brand-green">imaginations.</span>
          </h2>
          <p className="mt-5 text-lg text-brand-ink/80 leading-relaxed">
            For over <strong className="text-brand-red">20 years</strong> we&apos;ve welcomed Ridgeway&apos;s littlest learners into a safe, joyful space. 
            Most of our community has grown up right here — many parents were once our little learners too.

          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.label}
                  className="rounded-2xl border-[3px] border-brand-ink bg-white p-5 shadow-brutal-sm lift-hover"
                  data-testid={`about-value-${v.label.toLowerCase().replace(/\s|-/g, "")}`}
                >
                  <div className={`w-11 h-11 rounded-xl border-2 border-brand-ink flex items-center justify-center ${v.color}`}>
                    <Icon size={22} strokeWidth={2.5} className={v.color.includes("text-white") ? "text-white" : "text-brand-ink"} />
                  </div>
                  <p className="mt-3 font-display font-semibold text-lg text-brand-ink">{v.label}</p>
                  <p className="text-sm text-brand-ink/70 leading-relaxed mt-1">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
